import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Shield, Zap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function SurvivalModeToggle({ className = "", onToggle }) {
  const { user, refreshUser } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize from user settings or localStorage
  useEffect(() => {
    const localValue = localStorage.getItem('p2p_survival_mode') === 'true';
    const serverValue = user?.survival_mode ?? false;
    setEnabled(serverValue || localValue);
  }, [user?.survival_mode]);

  const handleToggle = async (checked) => {
    setEnabled(checked);
    localStorage.setItem('p2p_survival_mode', checked.toString());
    onToggle?.(checked);
    
    // Persist to server
    setSaving(true);
    try {
      await axios.put(`${API}/users/settings`, { 
        survival_mode: checked 
      }, { withCredentials: true });
      
      toast.success(checked 
        ? "Survival Mode ON - Focus on essentials" 
        : "Survival Mode OFF - Full view restored"
      );
      
      // Track event
      await axios.post(`${API}/events/track`, {
        event_name: checked ? 'survival_mode_enabled' : 'survival_mode_disabled',
        metadata: { source: 'dashboard_toggle' }
      }, { withCredentials: true });
      
      refreshUser?.();
    } catch (error) {
      console.error('Error saving survival mode:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div 
      className={`flex items-center justify-between bg-slate-50 border border-slate-200/50 rounded-xl px-3 py-2 ${className}`}
      data-guide="survival-toggle"
      data-testid="survival-mode-toggle"
    >
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-lg ${enabled ? 'bg-amber-100' : 'bg-slate-200/50'}`}>
          {enabled ? (
            <Zap className="w-3.5 h-3.5 text-amber-600" />
          ) : (
            <Shield className="w-3.5 h-3.5 text-slate-500" />
          )}
        </div>
        <div>
          <span className="text-sm font-medium text-p2p-gray">Survival Mode</span>
          {enabled && (
            <p className="text-[10px] text-amber-600 leading-tight">Only essentials & urgent bills</p>
          )}
        </div>
      </div>
      <Switch
        checked={enabled}
        onCheckedChange={handleToggle}
        disabled={saving}
        className="data-[state=checked]:bg-amber-500"
      />
    </div>
  );
}

// Export hook for other components to check survival mode
export const useSurvivalMode = () => {
  const { user } = useAuth();
  const localValue = localStorage.getItem('p2p_survival_mode') === 'true';
  return user?.survival_mode ?? localValue;
};
